/**
 * SES-8: "re-check on … any 401/409". Every client helper that talks to a
 * skill-matcher route hands the HTTP status of the answer to
 * `reportSessionResponse`; the board's session hook (`useSessionLifecycle`,
 * apps/tkms) listens and re-reads `GET /api/session`. 401 (no session, or an
 * expired one), 403 (no current consent) and 409 (this page's view is behind
 * the server) all mean the same thing to the page: what it shows may no
 * longer be true.
 *
 * Pure — a module-level listener set, no React, no env — so a plain fetch
 * helper can report without importing a hook. One page, one board, so a
 * module-level set is the right scope; on the server nothing subscribes and a
 * report is a no-op.
 */

const RECHECK_STATUSES: ReadonlySet<number> = new Set([401, 403, 409]);

const listeners = new Set<() => void>();

/** Subscribes to re-check requests; returns the unsubscribe. */
export function onSessionRecheck(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reports the status of a skill-matcher answer: a 401, 403 or 409 asks
 *  every listener to re-read the session, anything else says nothing about
 *  it. Every caller has a status to report — since SES-4 the upload's token
 *  step answers with the guard's own status too. */
export function reportSessionResponse(status: number): void {
  if (!RECHECK_STATUSES.has(status)) {
    return;
  }
  for (const listener of [...listeners]) {
    listener();
  }
}
